import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Footprints, QrCode } from "lucide-react";

export const GuardTour = () => {
  const [activeClockin, setActiveClockin] = useState(null);
  const [tour, setTour] = useState(null);
  const [checkpoints, setCheckpoints] = useState([]);

  const refresh = useCallback(async () => {
    const cis = await api.get("/clockins?active_only=true");
    const mine = cis.data[0];
    setActiveClockin(mine || null);
    if (mine) {
      const cps = await api.get(`/checkpoints?site_id=${mine.site_id}`);
      setCheckpoints(cps.data);
      const tours = await api.get("/tours");
      const active = tours.data.find(t => t.status === "ACTIVE" && t.site_id === mine.site_id);
      setTour(active || null);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const start = async () => {
    if (!activeClockin) return toast.error("Clock in first");
    const r = await api.post("/tours/start", { site_id: activeClockin.site_id });
    setTour(r.data);
    toast.success("Tour started");
  };

  const scan = async (cp) => {
    if (!tour) return;
    const r = await api.post(`/tours/${tour.id}/scan`, { checkpoint_id: cp.id, lat: cp.lat, lng: cp.lng });
    setTour(r.data);
    toast.success(`Scanned ${cp.name}`);
  };

  if (!activeClockin) {
    return (
      <div className="s-card text-center" style={{ padding: 32 }}>
        <Footprints size={32} color="#8CA0B8" style={{ margin: "0 auto 12px" }} />
        <div style={{ fontSize: 15, color: "#fff" }}>Clock in before starting a tour</div>
        <div className="text-muted" style={{ fontSize: 13, marginTop: 6 }}>Tours are tied to your active post.</div>
      </div>
    );
  }

  const scannedIds = new Set((tour?.scanned || []).map(s => s.checkpoint_id));
  const progress = tour ? (scannedIds.size / Math.max(1, checkpoints.length)) * 100 : 0;

  return (
    <div className="space-y-4">
      {!tour ? (
        <button onClick={start} className="btn-primary w-full tap-target" style={{ padding: 18, fontSize: 16 }} data-testid="start-tour">
          <Footprints size={18} /> Start patrol tour
        </button>
      ) : (
        <>
          <div className="s-card">
            <div className="text-muted" style={{ fontSize: 12 }}>Tour progress</div>
            <div className="h-page mt-1">{scannedIds.size} / {checkpoints.length}</div>
            <div style={{ background: "#1F3245", height: 8, borderRadius: 4, marginTop: 12 }}>
              <div style={{ width: `${progress}%`, background: "#2ECAC8", height: 8, borderRadius: 4, transition: "width 0.3s" }} />
            </div>
          </div>

          <div className="space-y-2">
            {checkpoints.map(cp => {
              const done = scannedIds.has(cp.id);
              return (
                <button key={cp.id} onClick={() => scan(cp)} disabled={done}
                        className="s-card w-full text-left flex items-center justify-between tap-target"
                        style={{ opacity: done ? 0.6 : 1 }} data-testid={`scan-${cp.id}`}>
                  <div className="flex items-center gap-3">
                    {done ? <CheckCircle2 size={20} color="#2D9E5F" /> : <QrCode size={20} color="#2ECAC8" />}
                    <div>
                      <div style={{ fontSize: 14, color: "#fff" }}>{cp.name}</div>
                      <div className="text-muted mono" style={{ fontSize: 11 }}>{cp.qr_code}</div>
                    </div>
                  </div>
                  <span className="text-muted" style={{ fontSize: 12 }}>{done ? "Scanned" : "Tap to scan"}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
